import { useLayoutEffect, useRef, useState } from "react";

const TAG_GAP = 4;
const MORE_BADGE_WIDTH = 70;

let measureContext: CanvasRenderingContext2D | null | undefined;
const getMeasureContext = () => {
    if (measureContext === undefined) {
        measureContext = document.createElement("canvas").getContext("2d");
    }
    return measureContext;
};

// Measures how many selected-value tags fit on one line of the trigger, so overflow
// collapses into a "+N" badge instead of wrapping or clipping. Tag chrome (padding,
// border, remove icon) is constant, so only the label text width varies; that's
// measured with canvas text metrics against a single rendered tag used for calibration,
// avoiding an offscreen clone of the whole tag list.
export const useVisibleTagCount = (values: string[], labels: Map<string, string>) => {
    const containerRef = useRef<HTMLSpanElement>(null);
    const firstTagRef = useRef<HTMLSpanElement>(null);
    const [visibleCount, setVisibleCount] = useState(values.length);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const firstTag = firstTagRef.current;
        if (!container || !firstTag || values.length === 0) {
            setVisibleCount(values.length);
            return;
        }

        const recalculate = () => {
            const ctx = getMeasureContext();
            const textWidth = (text: string) => {
                if (!ctx) return text.length * 6;
                ctx.font = getComputedStyle(firstTag).font;
                return ctx.measureText(text).width;
            };

            const firstLabel = labels.get(values[0]) ?? values[0];
            const chromeWidth = firstTag.offsetWidth - textWidth(firstLabel);
            const availableWidth = container.clientWidth;

            let used = 0;
            let count = 0;
            for (let i = 0; i < values.length; i++) {
                const label = labels.get(values[i]) ?? values[i];
                const width = chromeWidth + textWidth(label) + TAG_GAP;
                const reserve = i < values.length - 1 ? MORE_BADGE_WIDTH : 0;
                if (count > 0 && used + width + reserve > availableWidth) break;
                used += width;
                count++;
            }
            setVisibleCount(count);
        };

        recalculate();
        const observer = new ResizeObserver(recalculate);
        observer.observe(container);
        return () => observer.disconnect();
    }, [values, labels]);

    return { containerRef, firstTagRef, visibleCount };
}
